import { useEffect, useMemo, useState } from "react";
import { hieraApi } from "../lib/api";
import { t, type Lang } from "../lib/i18n";

type Mode = "move" | "copy";

type SpaceOpt = { id: string; title: string };
type PageOpt = { id: string; parent_id: string | null; title: string };

type Props = {
  lang: Lang;
  pageId: string;
  pageTitle: string;
  sourceSpaceId: string;
  initialMode: Mode;
  onClose: () => void;
  onDone: (result: { mode: Mode; pageId: string; spaceId: string }) => void;
};

export function PageRelocateDialog({
  lang,
  pageId,
  pageTitle,
  sourceSpaceId,
  initialMode,
  onClose,
  onDone,
}: Props) {
  const [mode, setMode] = useState<Mode>(initialMode);
  const [spaces, setSpaces] = useState<SpaceOpt[]>([]);
  const [spaceId, setSpaceId] = useState(sourceSpaceId);
  const [pages, setPages] = useState<PageOpt[]>([]);
  const [parentId, setParentId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let dead = false;
    hieraApi.spaces().then((res) => {
      if (dead) return;
      const list = (res.spaces || []).map((row) => ({
        id: String(row.id || ""),
        title: String(row.title || "Untitled"),
      })).filter((row) => row.id);
      setSpaces(list);
      if (!list.some((row) => row.id === spaceId) && list[0]) setSpaceId(list[0].id);
    });
    return () => {
      dead = true;
    };
  }, []);

  useEffect(() => {
    let dead = false;
    setParentId("");
    hieraApi.pages(spaceId).then((res) => {
      if (dead) return;
      setPages(
        res.pages
          .filter((row) => row.id !== pageId)
          .map((row) => ({ id: row.id, parent_id: row.parent_id, title: row.title })),
      );
    }).catch(() => {
      if (!dead) setPages([]);
    });
    return () => {
      dead = true;
    };
  }, [spaceId, pageId]);

  const parentOptions = useMemo(() => {
    const byId = new Map(pages.map((p) => [p.id, p]));
    function path(id: string): string {
      const parts: string[] = [];
      let cur = byId.get(id);
      let guard = 0;
      while (cur && guard < 40) {
        parts.unshift(cur.title || "Untitled");
        cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
        guard += 1;
      }
      return parts.join(" / ");
    }
    return pages
      .map((p) => ({ id: p.id, label: path(p.id) }))
      .sort((a, b) => a.label.localeCompare(b.label, lang === "ru" ? "ru" : "en"));
  }, [pages, lang]);

  async function submit() {
    setBusy(true);
    setError("");
    try {
      if (mode === "move") {
        const res = await hieraApi.movePage(pageId, {
          spaceId,
          parentId: parentId || null,
        });
        onDone({ mode, pageId: res.page.id, spaceId: res.page.space_id });
      } else {
        const res = await hieraApi.copyPage(pageId, {
          spaceId,
          parentId: parentId || null,
          titleSuffix: " copy",
        });
        onDone({ mode, pageId: res.page.id, spaceId: res.page.space_id });
      }
    } catch {
      setError(t(lang, "relocateFailed"));
      setBusy(false);
    }
  }

  return (
    <div className="modal-back" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal-head">
          <strong>{mode === "move" ? t(lang, "move") : t(lang, "copyTo")}</strong>
          <button type="button" onClick={onClose} aria-label="×">×</button>
        </div>
        <div className="modal-tabs">
          <button type="button" className={mode === "move" ? "is-on" : ""} onClick={() => setMode("move")}>
            {t(lang, "move")}
          </button>
          <button type="button" className={mode === "copy" ? "is-on" : ""} onClick={() => setMode("copy")}>
            {t(lang, "copyTo")}
          </button>
        </div>
        <div className="modal-body relocate-body">
          <p className="muted relocate-src">{pageTitle || "Untitled"}</p>
          <label>
            {t(lang, "spaces")}
            <select value={spaceId} onChange={(e) => setSpaceId(e.target.value)}>
              {spaces.map((sp) => (
                <option key={sp.id} value={sp.id}>{sp.title}</option>
              ))}
            </select>
          </label>
          <label>
            {t(lang, "relocateParent")}
            <select value={parentId} onChange={(e) => setParentId(e.target.value)}>
              <option value="">{t(lang, "moveRoot")}</option>
              {parentOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </label>
          {error ? <p className="form-error">{error}</p> : null}
          <div className="relocate-actions">
            <button type="button" className="btn-line" onClick={onClose} disabled={busy}>
              {t(lang, "cancel")}
            </button>
            <button type="button" className="btn" onClick={() => void submit()} disabled={busy || !spaceId}>
              {mode === "move" ? t(lang, "move") : t(lang, "copyTo")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
